import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Tabs, Box, Text, Flex, Button } from '@radix-ui/themes';
// api
import ApiKey from '../api/ApiKey';
import BaseApi from '../api/BaseApi';
// components
import Footer from './Footer';
import NavBar from './NavBar';

type Errors = { email: string };

type ForgotPasswordDto = {
   email?: string;
   contact?: string;
};

const ForgotPassword = () => {
   const { t } = useTranslation('global');
   const location = useLocation();

   const [activeTab, setActiveTab] = useState(() => {
      return location.state?.user || 'employer';
   });

   useEffect(() => {
      window.scrollTo(0, 0);
   }, []);

   return (
      <>
         <NavBar />
         <>
            <div className="aboutusSection1 text-center">
               <h1 className="">{t('forgotPassword.forgotPassword')}</h1>
               <h6 className="text-muted fw-normal">
                  {' '}
                  <Link to="/" style={{ color: 'grey' }}>
                     {t('navHeaders.home')}
                  </Link>
                  {' / '}
                  {t('forgotPassword.forgotPassword')}
               </h6>
            </div>

            <Box maxWidth={'600px'} style={{ margin: '0 auto' }}>
               <Tabs.Root
                  value={activeTab}
                  onValueChange={(tab) => {
                     setActiveTab(tab);
                  }}
               >
                  <Tabs.List size="2">
                     <Tabs.Trigger value="employer">
                        <Text size="4">Роботодавець</Text>
                     </Tabs.Trigger>
                     <Tabs.Trigger value="candidate">
                        <Text size="4">Шукач</Text>
                     </Tabs.Trigger>
                  </Tabs.List>

                  <Box pt="3">
                     <Tabs.Content value="employer">
                        <EmployerForgotPasswod />
                     </Tabs.Content>
                     <Tabs.Content value="candidate">
                        <CandidateForgotPasswod />
                     </Tabs.Content>
                  </Box>
               </Tabs.Root>
            </Box>
            <Footer />
         </>
      </>
   );
};

export default ForgotPassword;

function EmployerForgotPasswod() {
   const navigate = useNavigate();
   const { t } = useTranslation('global');
   const [errors, setErrors] = useState({ email: '' });
   const [formData, setFormData] = useState({ email: '' });
   const [loading, setLoading] = useState(false);
   const tokenKey = Cookies.get('tokenClient');

   const handleSubmit = async () => {
      const isValidEmail = (email: string) => {
         const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

         return emailPattern.test(email);
      };

      try {
         const newErrors: Errors = {
            email: '',
         };

         if (formData.email === '') {
            newErrors.email = "Електронна пошта обов'язкова";
            window.scrollTo(0, 0);
         }

         if (!!formData.email && !isValidEmail(formData.email)) {
            newErrors.email = t('jobseekerLogin.invalidEmail');
         }

         setErrors(newErrors);
         if (newErrors.email === '') {
            setLoading(true);

            const response = await axios.post(BaseApi + '/users/forgotPassword', formData, {
               headers: {
                  'Content-Type': 'application/json',
                  key: ApiKey,
                  token: tokenKey,
               },
            });
            setLoading(false);

            if (response.data.status === 200) {
               navigate('/user/employerlogin');

               Swal.fire({
                  icon: 'success',
                  title: response.data.message,
                  confirmButtonText: t('forgotPassword.close'),
               });
            } else if (response.data.status === 500) {
               Swal.fire({
                  icon: 'error',
                  title: response.data.message,
                  confirmButtonText: t('forgotPassword.close'),
               });
            } else {
               Swal.fire({
                  icon: 'warning',
                  title: response.data.message,
                  confirmButtonText: t('forgotPassword.close'),
               });
            }
         }
      } catch (error) {
         setLoading(false);

         Swal.fire({
            icon: 'error',
            text: t('forgotPassword.failedTxt'),
            title: t('forgotPassword.failedTitle'),
            confirmButtonText: t('forgotPassword.close'),
         });
      }
   };

   const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;

      setFormData((prevData) => ({
         ...prevData,
         [name]: value,
      }));
      setErrors((prev) => ({
         ...prev,
         [name]: '',
      }));
   };

   return (
      <div className="privacyPolicy">
         <div className="row">
            <div className="card">
               <div className="card-body">
                  <h2 className="text-center pb-4 pt-2">
                     {t('forgotPassword.forgot')}
                     <span className="textGradient">
                        {' '}
                        <span className="SubHaddingTxt"> {t('forgotPassword.password')}</span>
                     </span>
                  </h2>
                  <p className="forgotPasswordProcessTxt">{t('forgotPassword.text1')}</p>
                  <form
                     noValidate
                     onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                     }}
                  >
                     <div className="mb-2">
                        <input
                           type="email"
                           className={`form-control ${errors.email && 'input-error'}`}
                           id="exampleInputEmail1"
                           aria-describedby="emailHelp"
                           name="email"
                           value={formData.email}
                           placeholder={t('forgotPassword.emailAddress')}
                           onChange={handleChange}
                        />
                        {errors.email && <div className="text-danger">{errors.email}</div>}
                     </div>
                     <p className="forgotPasswordReturnLogin text-muted">
                        {t('forgotPassword.text2')}{' '}
                        <Link to="/user/employerlogin">{t('forgotPassword.loginPage')}</Link>
                     </p>
                     <Button
                        size="4"
                        loading={loading}
                        type="submit"
                        className="btn button1 w-100 mt-4 mb-3"
                     >
                        {t('forgotPassword.submitButton')}
                     </Button>
                  </form>
               </div>
            </div>
         </div>
      </div>
   );
}

function CandidateForgotPasswod() {
   const navigate = useNavigate();
   const { t } = useTranslation('global');
   const [errors, setErrors] = useState({ email: '' });
   const [formData, setFormData] = useState({ email: '' });
   const [loading, setLoading] = useState(false);
   const tokenKey = Cookies.get('tokenClient');

   const handleSubmit = async () => {
      const isValidPhone = (phone: string) => {
         const contactNumberFormat = /^\+\d{1,3}\d{6,14}$/;
         return contactNumberFormat.test(phone);
      };

      const isValidEmail = (email: string) => {
         const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

         return emailPattern.test(email);
      };

      try {
         const newErrors: Errors = {
            email: '',
         };

         if (formData.email === '') {
            newErrors.email = t('jobseekerLogin.loginRequired');
            window.scrollTo(0, 0);
         }

         if (!!formData.email && !isValidEmail(formData.email) && !isValidPhone(formData.email)) {
            newErrors.email = t('jobseekerLogin.invalidLogin');
         }

         setErrors(newErrors);
         if (newErrors.email === '') {
            setLoading(true);
            const dto: ForgotPasswordDto = {};

            if (isValidEmail(formData.email)) {
               dto.email = formData.email;
            }

            if (isValidPhone(formData.email)) {
               dto.contact = formData.email;
            }

            const response = await axios.post(BaseApi + '/users/forgotPassword', dto, {
               headers: {
                  'Content-Type': 'application/json',
                  key: ApiKey,
                  token: tokenKey,
               },
            });
            setLoading(false);

            if (response.data.status === 200) {
               navigate('/user/jobseekerlogin');

               Swal.fire({
                  icon: 'success',
                  title: response.data.message,
                  confirmButtonText: t('forgotPassword.close'),
               });
            } else if (response.data.status === 500) {
               Swal.fire({
                  icon: 'error',
                  title: response.data.message,
                  confirmButtonText: t('forgotPassword.close'),
               });
            } else {
               Swal.fire({
                  icon: 'warning',
                  title: response.data.message,
                  confirmButtonText: t('forgotPassword.close'),
               });
            }
         }
      } catch (error) {
         setLoading(false);

         Swal.fire({
            icon: 'error',
            text: t('forgotPassword.failedTxt'),
            title: t('forgotPassword.failedTitle'),
            confirmButtonText: t('forgotPassword.close'),
         });
      }
   };

   const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;

      setFormData((prevData) => ({
         ...prevData,
         [name]: value,
      }));
      setErrors((prev) => ({
         ...prev,
         [name]: '',
      }));
   };

   return (
      <div className="privacyPolicy">
         <div className="row">
            <div className="card">
               <div className="card-body">
                  <h2 className="text-center pb-4 pt-2">
                     {t('forgotPassword.forgot')}
                     <span className="textGradient">
                        {' '}
                        <span className="SubHaddingTxt"> {t('forgotPassword.password')}</span>
                     </span>
                  </h2>
                  <p className="forgotPasswordProcessTxt">
                     {"Введіть адресу електронної пошти або телефон, пов'язану з вашим акаунтом"}
                  </p>
                  <form
                     noValidate
                     onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                     }}
                  >
                     <div className="mb-2">
                        <input
                           type="email"
                           className={`form-control ${errors.email && 'input-error'}`}
                           id="exampleInputEmail1"
                           aria-describedby="emailHelp"
                           name="email"
                           value={formData.email}
                           placeholder={'Електронна пошта або телефон'}
                           onChange={handleChange}
                        />
                        {errors.email && <div className="text-danger">{errors.email}</div>}
                     </div>
                     <p className="forgotPasswordReturnLogin text-muted">
                        {t('forgotPassword.text2')}{' '}
                        <Link to="/user/jobseekerlogin">{t('forgotPassword.loginPage')}</Link>
                     </p>
                     <Button
                        size="4"
                        loading={loading}
                        type="submit"
                        className="btn button1 w-100 mt-4 mb-3"
                     >
                        {t('forgotPassword.submitButton')}
                     </Button>
                  </form>
               </div>
            </div>
         </div>
      </div>
   );
}
